import cx from 'classnames'
import { useEffect } from 'react'
import { useHistory, generatePath } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useSearchParams } from 'hooks/useSearchParams'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { UserClient } from 'api/clients/UserClient'
import { reportError } from 'utils/error'
import {
  LOGIN_PATH,
  CREATE_WORKSPACE_PATH,
  GOOGLE_PATH,
  FACEBOOK_PATH,
  APPLE_PATH,
  COMPANY_DATA_PATH,
  SELECT_WORKSPACE_PATH,
  SIGNUP_PATH,
  VIRTUAL_MARKETER_ONBOARDING_PATH,
} from 'paths'
import { authFromResponse } from 'utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { MetaTags } from 'components/MetaTags'
import Styles from './Landing.module.scss'
import { RegistrationType, trackRegistrationStart, hashEmailToPercentage } from 'screens/LoginOrSignup/utils'
import useLoginOrSignUp from 'screens/LoginOrSignup/hooks/useLoginOrSignUp'
import { SIGNED_UP_EVENT, trackEvent } from 'utils/trackingUtils'
import { Spinner } from 'common/Spinner/Spinner'
import { Auth } from 'types'
import {
  AUTH_TOKEN_PARAM,
  PROMOTION_CODE_PARAM,
  REDIRECT_URL_PARAM,
  REGISTRATION_TYPE_PARAM,
  WORKSPACE_INVITE_TOKEN_PARAM,
} from 'screens/LoginOrSignup/constants'
import { useBrand } from 'hooks/useBrand'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useGeolocation } from 'hooks/queries/useGeolocation'
import { useSignUp } from 'screens/Registration/hooks/useSignUp'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { EMAIL_HASH_PERCENTAGE, NEW_REGISTRATION_COUNTRIES } from 'screens/LoginOrSignup/LoginOrSignup'
import { IS_VM_REGISTRATION_KEY } from 'flows/common/steps/Registration/constants'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'

const NEXT_PAGE_TO_PATH = {
  CREATE_WORKSPACE: CREATE_WORKSPACE_PATH,
  COMPANY_DATA: COMPANY_DATA_PATH,
}

type OauthLandingProps = {
  provider: RegistrationType
}

export const OauthLanding = observer(({ provider }: OauthLandingProps) => {
  const params = useSearchParams()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { onLoginOrSignUpSuccess } = useLoginOrSignUp()
  const { onSignUpSuccess } = useSignUp()
  const brand = useBrand()
  const featureFlagsStore = useRootStore().featureFlags
  const { trackSignupMethodSelected } = useOnboardingTracking()

  // Add hooks for geolocation and feature flags
  const { data: geolocationRequest, isLoading } = useGeolocation()
  const userCountry = geolocationRequest?.location_country_code
  const geoLocationEnabled = useFlipperFlag('geo_location_registration')
  const newRegistrationEnabled = useFlipperFlag('new_registration')

  useEffect(() => {
    if (!featureFlagsStore.ready) {
      return
    }
    const fetchLoggedInUser = async () => {
      const token = params.get(AUTH_TOKEN_PARAM)

      if (!token) return

      const redirectTo = params.get(REDIRECT_URL_PARAM)
      const inviteToken = params.get(WORKSPACE_INVITE_TOKEN_PARAM)
      const promotionCode = params.get(PROMOTION_CODE_PARAM)
      const newRegistrationOverride = params.get('new_registration_override')

      if (!redirectTo) params.delete(REDIRECT_URL_PARAM)
      params.delete(WORKSPACE_INVITE_TOKEN_PARAM)
      params.delete(AUTH_TOKEN_PARAM)

      const authInfoOptions = {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
      const response = await client.getAuthInfo(authInfoOptions)

      if (response.type === SUCCESS_RESPONSE_TYPE) {
        const auth = authFromResponse((response.data as { user: Auth }).user, authInfoOptions.headers)

        const geoMatch = geoLocationEnabled && NEW_REGISTRATION_COUNTRIES.includes(userCountry || '')

        let isNewRegistration = false
        if (newRegistrationEnabled || newRegistrationOverride === 'true') {
          isNewRegistration = true
        } else if (geoMatch) {
          const emailHashValue = hashEmailToPercentage(auth.email)
          isNewRegistration = !!(auth.email && emailHashValue < EMAIL_HASH_PERCENTAGE)
        }

        const isVmRegistration = localStorage.getItem(IS_VM_REGISTRATION_KEY)
        localStorage.removeItem(IS_VM_REGISTRATION_KEY)

        if (isNewRegistration || isVmRegistration) {
          const { landingWorkspace, privateWorkspaces } = await onSignUpSuccess(auth, inviteToken || undefined)

          trackSignupMethodSelected(provider.toLowerCase())

          const searchParams = new URLSearchParams()
          if (redirectTo) searchParams.append('redirectTo', redirectTo)
          if (inviteToken) searchParams.append('workspaceInviteToken', inviteToken)
          if (promotionCode) searchParams.append('promotionCode', promotionCode)
          if (landingWorkspace) searchParams.append('workspaceId', landingWorkspace.id.toString())

          searchParams.append('email', auth.email)

          trackRegistrationStart(provider)

          if (
            (privateWorkspaces || []).length > 1 ||
            landingWorkspace?.is_virtual_marketer_onboarding_completed ||
            !landingWorkspace?.is_virtual_marketer
          ) {
            return history.push({
              pathname: SELECT_WORKSPACE_PATH,
              search: searchParams.toString(),
            })
          }

          const regDataRes = await UserClient.getRegistrationData()
          if (regDataRes.type === SUCCESS_RESPONSE_TYPE && regDataRes.data?.user_registration_data) {
            const regData = regDataRes.data.user_registration_data

            if (regData.discovery_source && regData.business_type) {
              return history.push({
                pathname: generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, {
                  workspaceId: landingWorkspace.id.toString(),
                }),
                search: searchParams.toString(),
              })
            }
          }

          return history.push({
            pathname: SIGNUP_PATH,
            search: searchParams.toString(),
          })
        }

        // Otherwise use the old flow
        const nextPage = await onLoginOrSignUpSuccess(
          auth,
          provider,
          redirectTo || undefined,
          inviteToken || undefined,
          promotionCode || undefined,
        )

        queryClient.invalidateQueries('featureFlags')

        if (!nextPage) return

        const path: string = NEXT_PAGE_TO_PATH[nextPage]

        if (!auth.signed_up) {
          trackRegistrationStart(provider)
          trackEvent(SIGNED_UP_EVENT, undefined, { email: auth.email })
          params.append(REGISTRATION_TYPE_PARAM, provider)
        }

        return history.push({ pathname: path, search: params.toString() })
      }

      history.push(LOGIN_PATH)
      showToastMessage('Invalid user token', 'error')
      reportError(`Error fetching ${provider} user information`, { severity: 'error', metaData: response.data })
    }

    if (isLoading) return

    fetchLoggedInUser()
  }, [
    history,
    params,
    onLoginOrSignUpSuccess,
    queryClient,
    provider,
    geoLocationEnabled,
    newRegistrationEnabled,
    userCountry,
    onSignUpSuccess,
    isLoading,
    featureFlagsStore.ready,
    trackSignupMethodSelected,
  ])

  const getProviderPath = () => {
    if (provider === RegistrationType.Google) return GOOGLE_PATH
    if (provider === RegistrationType.Apple) return APPLE_PATH
    return FACEBOOK_PATH
  }

  return (
    <>
      <MetaTags
        url={getProviderPath()}
        title={`${brand.name} | ${provider} Authentication`}
        description={`Blaze's ${provider} authentication`}
      />
      <div className={cx(Styles.container)}>
        <Spinner />
      </div>
    </>
  )
})

export default OauthLanding
